// src/main.js
//Careful when importing here, please keep the order of imports as is, some of these are very sensitive to order and will break if changed
//Please make sure there is no racing condition when importing
import { createApp } from "vue";
import { createPinia } from "pinia";
import { createRouter, createWebHistory } from "vue-router";
import { EventType } from "@azure/msal-browser";
import { datadogRum } from "@datadog/browser-rum";

import "./assets/tailwind.css";
import "./assets/sass/style.scss";
import "./assets/reset.css";
import "@vuepic/vue-datepicker/dist/main.css";

import { msalInstance, waitForMsalReady } from "@/msal/boot";
import featureFlags from "@/plugins/featureFlags";

(async () => {
  // 1) MSAL must be ready BEFORE anything else touches it
  await waitForMsalReady();

  //The order of these imports are extremely important, be very careful if you are changing anything here
  const { initializeAuthRedirect } = await import("@/msal/authRedirect");

  await initializeAuthRedirect();

  //Careful when importing here, please keep the order of imports as is, some of these are very sensitive to order and will break if changed
  //These imports are set to prevent racing condition
  const { default: App } = await import("./App.vue");
  const { default: routes } = await import("./router/routes");
  const { msalPlugin } = await import("./plugins/msalPlugin");
  const { registerGuard } = await import("./router/Guard");
  const { CustomNavigationClient } = await import("./router/NavigationClient");
  const { useNavBarOptionsStore } = await import("@/stores/NavBarOptionsStore");
  const { useDataStore } = await import("@/stores/DataStore");

  // 2) Datadog (skip on localhost)
  if (window.location.hostname !== "localhost") {
    datadogRum.init({
      applicationId: process.env.VUE_APP_DD_APPID,
      clientToken: process.env.VUE_APP_DD_CLIENT_TOKEN,
      site: "datadoghq.eu",
      service: "kinetikiq-dashboard",
      env: "prod",
      version: process.env.VUE_APP_VERSION,
      allowedTracingUrls: [
        "https://kinetikiq.com",
        (url) => url.startsWith("https://kinetikiq.com"),
        (url) => url.startsWith("https://www.kinetikiq.com"),
        (url) => url.startsWith("https://staging-pst.azurefd.net"),
        (url) => url.startsWith("https://kinetikiq.azurefd.net"),
        (url) => url.startsWith("https://api.kinetikiq.com"),
        (url) => url.startsWith("https://staging-api.kinetikiq.com"),
        (url) => url.startsWith("https://hub.kinetikiq.com"),
        (url) => url.startsWith("https://staging.kinetikiq.com"),
        (url) => url.startsWith("https://dashboard.kinetikiq.com"),
        (url) => url.startsWith("https://app.kinetikiq.com"),
      ],
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask",
    });
  }

  // 3) Vue app + stores/plugins
  const app = createApp(App);
  const pinia = createPinia();
  app.use(pinia);
  app.use(featureFlags);

  // 4) kick off feature loading and expose the PROMISE right away
  const featuresPromise =
    (window.$features && window.$features.then ? window.$features : null) ||
    app.config.globalProperties
      .$loadFeatures([
        "longitudinal-tracking",
        "public-exercise-page",
        "library-multiple-tabs",
      ])
      .catch(() => ({})); // Never reject
  window.$features = featuresPromise;

  // 5) Router (create first, then register guards)
  const router = createRouter({
    history: createWebHistory(),
    routes,
  });
  app.use(router);
  window.__appRouter = router;

  // Handle ChunkLoadErrors (deployment mismatches)
  const handleChunkError = () => {
    const hasReloaded = sessionStorage.getItem("last-chunk-error-reload");
    const now = Date.now();

    // If we haven't reloaded in the last 10 seconds, try a reload
    if (!hasReloaded || now - parseInt(hasReloaded, 10) > 10000) {
      sessionStorage.setItem("last-chunk-error-reload", now.toString());
      window.location.reload();
    }
  };

  const isChunkLoadFailure = (value) => {
    if (!value) return false;

    const messages = [
      value?.message,
      value?.reason?.message,
      value?.error?.message,
      value?.target?.src,
      value?.target?.href,
      value?.request,
      typeof value?.reason === "string" ? value.reason : null,
    ]
      .filter(Boolean)
      .map((entry) => String(entry).toLowerCase());

    return messages.some(
      (msg) =>
        msg.includes("chunkloaderror") ||
        msg.includes("loading chunk") ||
        msg.includes("loading css chunk") ||
        (msg.includes("chunk") && msg.includes("failed")) ||
        (/\.(js|css)(\?|#|$)/.test(msg) && (msg.includes("/css/") || msg.includes("/js/")) && msg.includes("vue"))
    );
  };

  router.onError((error) => {
    if (isChunkLoadFailure(error)) {
      handleChunkError();
    }
  });

  window.addEventListener(
    "error",
    (e) => {
      if (isChunkLoadFailure(e)) {
        handleChunkError();
      }
    },
    true,
  );

  // Some chunk failures are surfaced as rejected dynamic-import promises.
  window.addEventListener("unhandledrejection", (e) => {
    if (isChunkLoadFailure(e)) {
      handleChunkError();
    }
  });

  // MSAL navigation client (lets MSAL drive Vue Router)
  const navigationClient = new CustomNavigationClient(router);
  msalInstance.setNavigationClient(navigationClient);

  // Guard (safe to use now that MSAL is hydrated)
  registerGuard(router, msalInstance);

  // 6) Keep MSAL active account in sync and set Datadog user
  const dataStore = useDataStore();
  dataStore.on("customerData", () => {
    const user = dataStore.currentCustomer;
    if (user && user.userId) {
      datadogRum.setUser({
        id: user.userId,
        name: user.fullName,
        email: user.email,
        plan: user.tier,
      });
    }
  });

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
    dataStore.fetchCustomerTierMetadata();
  }

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      // payload is AuthenticationResult
      msalInstance.setActiveAccount(event.payload.account);
      dataStore.fetchCustomerTierMetadata(true);
    }
  });

  // 7) Provide PCA to the app (plugin defers first access internally)
  app.use(msalPlugin, msalInstance);

  // 8) UI nicety: keep navbar selection in sync with route meta
  const navbarOptionsStore = useNavBarOptionsStore();
  router.beforeEach((to, _from, next) => {
    if (to.meta?.selectedNavbarItem) {
      navbarOptionsStore.setSelected(to.meta.selectedNavbarItem);
    }
    next();
  });

  await router.isReady();
  app.mount("#app");
})();
